/**
 * V44: 인사관리 > 재직현황 페이지
 *
 * 선택한 기간(startDate~endDate) 동안 재직했던 직원 리스트를 확인합니다.
 *
 * **기간 포함 기준:**
 * - 기간과 재직기간이 "하루라도 겹치면 포함" (중간에 입사/퇴사해도 포함)
 * - endDate가 없으면 "현재까지 재직"으로 간주
 *
 * **기능:**
 * - 시작일, 종료일 선택 (필수)
 * - 필터: 재직상태(status), 고용형태(employmentTypeCode)
 * - 결과: 직원 목록 테이블
 * - 엑셀 다운로드 (선택 직원)
 *
 * **권한:** HR(role_hr) 또는 system_admin
 */

import React, { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import {
  Loader,
  AlertCircle,
  Calendar,
  Search,
  Download,
  Filter,
  ChevronDown,
  Users,
  CheckSquare,
  Square,
  X,
} from 'lucide-react';
import * as XLSX from 'xlsx';

// ============================================
// 타입 정의
// ============================================

interface EmployeeInPeriod {
  id: string;
  employeeNumber: string;
  nameKo: string;
  email: string;
  orgUnitName?: string;
  positionTitle?: string;
  jobTitle?: string;
  status: string;
  currentEmploymentType?: string;
  matchedHistoriesSummary: string;
  // V77: 스냅샷 관련 필드
  hasSnapshot?: boolean;
  leaveDate?: string;
}

interface QueryResponse {
  success: boolean;
  employees: EmployeeInPeriod[];
  total: number;
  period: {
    startDate: string;
    endDate: string;
  };
}

interface ExportResponse {
  success: boolean;
  data: any[];
  filename: string;
  totalCount: number;
}

// ============================================
// 상수
// ============================================

const STATUS_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'active', label: '재직' },
  { value: 'on_leave', label: '휴직' },
  { value: 'terminated', label: '퇴사' },
];

const EMPLOYMENT_TYPE_OPTIONS = [
  { value: 'DISPATCH', label: '파견직' },
  { value: 'INTERN', label: '인턴' },
  { value: 'CONTRACT', label: '계약직' },
  { value: 'REGULAR', label: '정규직' },
];

const STATUS_LABELS: Record<string, string> = {
  active: '재직',
  on_leave: '휴직',
  terminated: '퇴사',
};

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  active: { bg: '#dcfce7', text: '#166534' },
  on_leave: { bg: '#fef3c7', text: '#92400e' },
  terminated: { bg: '#fee2e2', text: '#dc2626' },
};

// ============================================
// 헬퍼 함수
// ============================================

function getTodayString(): string {
  return new Date().toISOString().slice(0, 10);
}

function getYearStartString(): string {
  const now = new Date();
  return `${now.getFullYear()}-01-01`;
}

// ============================================
// 메인 컴포넌트
// ============================================

export function HrEmploymentStatusPage() {
  // ========================================
  // State
  // ========================================

  // 조회 기간
  const [startDate, setStartDate] = useState<string>(getYearStartString());
  const [endDate, setEndDate] = useState<string>(getTodayString());

  // 필터
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedEmploymentTypes, setSelectedEmploymentTypes] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  // 조회 결과
  const [employees, setEmployees] = useState<EmployeeInPeriod[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasQueried, setHasQueried] = useState(false);

  // 선택 상태 (엑셀 다운로드용)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // 엑셀 다운로드 상태
  const [isExporting, setIsExporting] = useState(false);

  // ========================================
  // 조회 핸들러
  // ========================================

  const handleQuery = useCallback(async () => {
    // 입력 검증
    if (!startDate || !endDate) {
      setError('시작일과 종료일을 모두 입력해주세요.');
      return;
    }

    if (startDate > endDate) {
      setError('시작일은 종료일보다 이전이어야 합니다.');
      return;
    }

    if (!functions) {
      setError('Firebase Functions가 초기화되지 않았습니다.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      setSelectedIds(new Set());

      const getEmployeesEmployedInPeriod = httpsCallable<any, QueryResponse>(
        functions,
        'getEmployeesEmployedInPeriod'
      );

      const result = await getEmployeesEmployedInPeriod({
        startDate,
        endDate,
        status: statusFilter || undefined,
        employmentTypeCodes: selectedEmploymentTypes.length > 0 ? selectedEmploymentTypes : undefined,
      });

      if (result.data.success) {
        setEmployees(result.data.employees);
        setHasQueried(true);
      } else {
        setError('조회에 실패했습니다.');
      }
    } catch (err: any) {
      console.error('재직현황 조회 실패:', err);
      setError(err.message || '조회 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, statusFilter, selectedEmploymentTypes]);

  // ========================================
  // 선택 핸들러
  // ========================================

  const handleSelectAll = () => {
    if (selectedIds.size === employees.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(employees.map((e) => e.id)));
    }
  };

  const handleToggleSelect = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  // ========================================
  // 엑셀 다운로드 핸들러
  // ========================================

  const handleExport = async () => {
    if (selectedIds.size === 0) {
      alert('다운로드할 직원을 선택해주세요.');
      return;
    }

    if (!functions) {
      alert('Firebase Functions가 초기화되지 않았습니다.');
      return;
    }

    try {
      setIsExporting(true);

      const exportEmployees = httpsCallable<any, ExportResponse>(
        functions,
        'exportEmployees'
      );

      const result = await exportEmployees({
        employeeIds: Array.from(selectedIds),
      });

      if (result.data.success && result.data.data.length > 0) {
        // 엑셀 컬럼 헤더 매핑
        const COLUMN_HEADERS: Record<string, string> = {
          employeeNumber: '사번',
          nameKo: '이름(한글)',
          email: '이메일',
          phoneNumber: '연락처',
          gender: '성별',
          birthDate: '생년월일',
          nationalityType: '내/외국인',
          residentRegistrationNo: '주민등록번호',
          address: '주소',
          educationLevel: '최종학력',
          major: '전공',
          certificatesText: '자격증',
          trainingsText: '교육이수',
          commentText: '비고',
          orgUnitName: '소속',
          positionTitle: '직위',
          jobTitle: '직책',
          status: '재직상태',
          currentEmploymentType: '현재고용형태',
          firstHireDate: '최초입사일',
          ngleFirstHireDate: '엔글최초입사일',
          currentEmploymentStartDate: '계약시작일', // V67
          probationEndDate: '수습만료일',
          dispatchHireDate: '파견입사일',
          dispatchEndDate: '파견만료일',
          internHireDate: '인턴입사일',
          internEndDate: '인턴만료일',
          contractHireDate: '계약입사일',
          contractEndDate: '계약만료일',
          regularHireDate: '정규입사일',
          leaveDate: '퇴사일',
          lastPromotionDate: '최종진급일',
          currentPositionStartDate: '현직위시작일',
        };

        // 컬럼 순서 정의
        const columnOrder = [
          'employeeNumber',
          'nameKo',
          'email',
          'phoneNumber',
          'gender',
          'birthDate',
          'nationalityType',
          'residentRegistrationNo',
          'address',
          'educationLevel',
          'major',
          'certificatesText',
          'trainingsText',
          'commentText',
          'orgUnitName',
          'positionTitle',
          'jobTitle',
          'status',
          'currentEmploymentType',
          'firstHireDate',
          'ngleFirstHireDate',
          'currentEmploymentStartDate', // V67
          'probationEndDate',
          'dispatchHireDate',
          'dispatchEndDate',
          'internHireDate',
          'internEndDate',
          'contractHireDate',
          'contractEndDate',
          'regularHireDate',
          'leaveDate',
          'lastPromotionDate',
          'currentPositionStartDate',
        ];

        // 데이터 변환
        const exportData = result.data.data.map((row: any) => {
          const newRow: Record<string, string> = {};
          columnOrder.forEach((key) => {
            const header = COLUMN_HEADERS[key] || key;
            newRow[header] = row[key] || '';
          });
          return newRow;
        });

        // 워크시트 생성
        const ws = XLSX.utils.json_to_sheet(exportData);

        // 컬럼 너비 설정
        const colWidths = columnOrder.map((key) => ({
          wch: Math.max((COLUMN_HEADERS[key] || key).length * 2, 12),
        }));
        ws['!cols'] = colWidths;

        // 워크북 생성
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, '재직현황');

        // 파일명 생성
        const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        const filename = `재직현황_${startDate}_${endDate}_${dateStr}.xlsx`;

        // 다운로드
        XLSX.writeFile(wb, filename);
      } else {
        alert('다운로드할 데이터가 없습니다.');
      }
    } catch (err: any) {
      console.error('엑셀 다운로드 실패:', err);
      alert(err.message || '엑셀 다운로드 중 오류가 발생했습니다.');
    } finally {
      setIsExporting(false);
    }
  };

  // ========================================
  // 고용형태 필터 토글
  // ========================================

  const handleToggleEmploymentType = (code: string) => {
    setSelectedEmploymentTypes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  // ========================================
  // 필터 초기화
  // ========================================

  const handleResetFilters = () => {
    setStatusFilter('');
    setSelectedEmploymentTypes([]);
  };

  // V60.x: 선택된 개별 필터 옵션 개수 합산 (그룹 단위 → 옵션 단위)
  const activeFilterCount =
    (statusFilter ? 1 : 0) + selectedEmploymentTypes.length;

  // ========================================
  // 렌더링
  // ========================================

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
      {/* 페이지 헤더 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}
      >
        <div>
          <div>
            <h1
              style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: 'var(--color-gray-800)',
              }}
            >
              재직 현황
            </h1>
            <p style={{ fontSize: '14px', color: 'var(--color-gray-500)' }}>
              선택한 기간 동안 재직했던 직원 목록을 조회합니다.
            </p>
          </div>
        </div>
      </div>

      {/* 조회 조건 패널 */}
      <div
        style={{
          background: 'white',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        {/* 기간 선택 */}
        <div style={{ marginBottom: '16px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: 'var(--color-gray-700)',
              marginBottom: '8px',
            }}
          >
            조회 기간 (필수)
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                padding: '10px 14px',
                border: '1px solid var(--color-gray-200)',
                borderRadius: '8px',
                fontSize: '14px',
                width: '160px',
              }}
            />
            <span style={{ color: 'var(--color-gray-500)' }}>~</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{
                padding: '10px 14px',
                border: '1px solid var(--color-gray-200)',
                borderRadius: '8px',
                fontSize: '14px',
                width: '160px',
              }}
            />
            <button
              onClick={handleQuery}
              disabled={isLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 24px',
                background: isLoading
                  ? 'var(--color-gray-300)'
                  : 'var(--color-primary)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
              }}
            >
              {isLoading ? (
                <>
                  <Loader
                    size={16}
                    style={{ animation: 'spin 1s linear infinite' }}
                  />
                  조회 중...
                </>
              ) : (
                <>
                  <Search size={16} />
                  조회
                </>
              )}
            </button>
          </div>
        </div>

        {/* 필터 버튼 */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              background: showFilters
                ? 'var(--color-primary-lighter)'
                : 'var(--color-gray-50)',
              border: '1px solid var(--color-gray-200)',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <Filter size={16} />
            <span>필터</span>
            {activeFilterCount > 0 && (
              <span
                style={{
                  background: 'var(--color-primary)',
                  color: 'white',
                  borderRadius: '10px',
                  padding: '2px 8px',
                  fontSize: '12px',
                }}
              >
                {activeFilterCount}
              </span>
            )}
            <ChevronDown
              size={16}
              style={{
                transform: showFilters ? 'rotate(180deg)' : 'rotate(0)',
                transition: 'transform 0.2s',
              }}
            />
          </button>

          {activeFilterCount > 0 && (
            <button
              onClick={handleResetFilters}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '8px 12px',
                background: 'transparent',
                border: 'none',
                color: 'var(--color-gray-500)',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              <X size={14} />
              필터 초기화
            </button>
          )}
        </div>

        {/* 필터 옵션 */}
        {showFilters && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '16px',
              marginTop: '16px',
              paddingTop: '16px',
              borderTop: '1px solid var(--color-gray-100)',
            }}
          >
            {/* 재직상태 필터 */}
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  color: 'var(--color-gray-500)',
                  marginBottom: '4px',
                }}
              >
                재직상태 (현재 기준)
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--color-gray-200)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'white',
                }}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* 고용형태 필터 */}
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  color: 'var(--color-gray-500)',
                  marginBottom: '4px',
                }}
              >
                고용형태 (기간 중 해당 형태로 근무)
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {EMPLOYMENT_TYPE_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '6px 12px',
                      background: selectedEmploymentTypes.includes(opt.value)
                        ? 'var(--color-primary-lighter)'
                        : 'var(--color-gray-50)',
                      border: selectedEmploymentTypes.includes(opt.value)
                        ? '1px solid var(--color-primary)'
                        : '1px solid var(--color-gray-200)',
                      borderRadius: '16px',
                      cursor: 'pointer',
                      fontSize: '13px',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedEmploymentTypes.includes(opt.value)}
                      onChange={() => handleToggleEmploymentType(opt.value)}
                      style={{ display: 'none' }}
                    />
                    <span
                      style={{
                        color: selectedEmploymentTypes.includes(opt.value)
                          ? 'var(--color-primary)'
                          : 'var(--color-gray-700)',
                        fontWeight: selectedEmploymentTypes.includes(opt.value)
                          ? '600'
                          : '400',
                      }}
                    >
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div
          style={{
            padding: '16px',
            marginBottom: '24px',
            background: '#fef2f2',
            border: '1px solid #ef4444',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            color: '#dc2626',
          }}
        >
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* 결과 영역 */}
      {hasQueried && !isLoading && (
        <>
          {/* 결과 헤더 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Users size={20} style={{ color: 'var(--color-gray-600)' }} />
              <span style={{ fontSize: '16px', fontWeight: '600', color: 'var(--color-gray-800)' }}>
                조회 결과: {employees.length}명
              </span>
              {selectedIds.size > 0 && (
                <span
                  style={{
                    padding: '4px 12px',
                    background: 'var(--color-primary-lighter)',
                    color: 'var(--color-primary)',
                    borderRadius: '16px',
                    fontSize: '13px',
                    fontWeight: '500',
                  }}
                >
                  {selectedIds.size}명 선택됨
                </span>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleSelectAll}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 16px',
                  background: 'white',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                {selectedIds.size === employees.length && employees.length > 0 ? (
                  <>
                    <CheckSquare size={16} />
                    전체 해제
                  </>
                ) : (
                  <>
                    <Square size={16} />
                    전체 선택
                  </>
                )}
              </button>
              <button
                onClick={handleExport}
                disabled={isExporting || selectedIds.size === 0}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 16px',
                  background:
                    isExporting || selectedIds.size === 0
                      ? 'var(--color-gray-300)'
                      : '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor:
                    isExporting || selectedIds.size === 0
                      ? 'not-allowed'
                      : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                {isExporting ? (
                  <>
                    <Loader
                      size={16}
                      style={{ animation: 'spin 1s linear infinite' }}
                    />
                    다운로드 중...
                  </>
                ) : (
                  <>
                    <Download size={16} />
                    엑셀 다운로드
                  </>
                )}
              </button>
            </div>
          </div>

          {/* 결과 테이블 */}
          {employees.length > 0 ? (
            <div
              style={{
                background: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                overflow: 'hidden',
              }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--color-gray-50)' }}>
                    <th style={{ ...thStyle, width: '50px' }}>
                      <input
                        type="checkbox"
                        checked={
                          selectedIds.size === employees.length &&
                          employees.length > 0
                        }
                        onChange={handleSelectAll}
                        style={{
                          width: '18px',
                          height: '18px',
                          cursor: 'pointer',
                        }}
                      />
                    </th>
                    <th style={{ ...thStyle, width: '80px' }}>사번</th>
                    <th style={{ ...thStyle, textAlign: 'left' }}>이름</th>
                    <th style={{ ...thStyle, textAlign: 'left' }}>이메일</th>
                    <th style={{ ...thStyle, textAlign: 'left' }}>소속</th>
                    <th style={{ ...thStyle, textAlign: 'left' }}>직위</th>
                    <th style={{ ...thStyle, textAlign: 'left' }}>직책</th>
                    <th style={{ ...thStyle, width: '80px' }}>현재상태</th>
                    <th style={{ ...thStyle, textAlign: 'left' }}>
                      기간 중 고용이력
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((emp) => {
                    const isSelected = selectedIds.has(emp.id);
                    const statusColor = STATUS_COLORS[emp.status] || {
                      bg: '#f3f4f6',
                      text: '#374151',
                    };

                    return (
                      <tr
                        key={emp.id}
                        style={{
                          background: isSelected
                            ? 'var(--color-primary-lighter)'
                            : 'white',
                          transition: 'background 0.2s',
                        }}
                        onClick={() => handleToggleSelect(emp.id)}
                      >
                        <td
                          style={tdStyle}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleSelect(emp.id)}
                            style={{
                              width: '18px',
                              height: '18px',
                              cursor: 'pointer',
                            }}
                          />
                        </td>
                        <td
                          style={{
                            ...tdStyle,
                            color: 'var(--color-gray-500)',
                            fontSize: '13px',
                          }}
                        >
                          {emp.employeeNumber || '-'}
                        </td>
                        <td style={tdStyle}>
                          <div
                            style={{
                              fontWeight: '500',
                              color: 'var(--color-gray-800)',
                            }}
                          >
                            {emp.nameKo}
                          </div>
                        </td>
                        <td
                          style={{
                            ...tdStyle,
                            color: 'var(--color-gray-600)',
                            fontSize: '13px',
                          }}
                        >
                          {emp.email}
                        </td>
                        <td style={{ ...tdStyle, color: 'var(--color-gray-600)' }}>
                          {emp.orgUnitName || '-'}
                        </td>
                        <td style={{ ...tdStyle, color: 'var(--color-gray-600)' }}>
                          {emp.positionTitle || '-'}
                        </td>
                        <td style={{ ...tdStyle, color: 'var(--color-gray-600)' }}>
                          {emp.jobTitle || '-'}
                        </td>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '4px 10px',
                                background: statusColor.bg,
                                color: statusColor.text,
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '500',
                              }}
                            >
                              {STATUS_LABELS[emp.status] || emp.status}
                            </span>
                            {/* V77: 퇴사자인데 스냅샷이 없는 경우 경고 배지 */}
                            {emp.status === 'terminated' && emp.hasSnapshot === false && (
                              <span
                                style={{
                                  display: 'inline-block',
                                  padding: '2px 6px',
                                  background: '#fef3c7',
                                  color: '#92400e',
                                  borderRadius: '4px',
                                  fontSize: '10px',
                                  fontWeight: '500',
                                }}
                                title="스냅샷이 없어 현재 데이터로 표시됩니다"
                              >
                                스냅샷 없음
                              </span>
                            )}
                          </div>
                        </td>
                        <td
                          style={{
                            ...tdStyle,
                            color: 'var(--color-gray-600)',
                            fontSize: '12px',
                            maxWidth: '300px',
                          }}
                        >
                          <span
                            title={emp.matchedHistoriesSummary}
                            style={{
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {emp.matchedHistoriesSummary}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div
              style={{
                background: 'white',
                borderRadius: '8px',
                padding: '60px 20px',
                textAlign: 'center',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              }}
            >
              <Users
                size={48}
                style={{
                  marginBottom: '12px',
                  color: 'var(--color-gray-300)',
                }}
              />
              <p style={{ color: 'var(--color-gray-500)' }}>
                해당 기간에 재직한 직원이 없습니다.
              </p>
            </div>
          )}
        </>
      )}

      {/* 초기 안내 */}
      {!hasQueried && !isLoading && (
        <div
          style={{
            background: 'white',
            borderRadius: '8px',
            padding: '60px 20px',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          <Calendar
            size={48}
            style={{
              marginBottom: '12px',
              color: 'var(--color-gray-300)',
            }}
          />
          <p style={{ color: 'var(--color-gray-500)', marginBottom: '8px' }}>
            조회 기간을 선택하고 [조회] 버튼을 클릭하세요.
          </p>
          <p style={{ color: 'var(--color-gray-400)', fontSize: '13px' }}>
            기간과 재직기간이 하루라도 겹치면 해당 직원이 조회됩니다.
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================
// 스타일
// ============================================

const thStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontSize: '13px',
  fontWeight: '600',
  color: 'var(--color-gray-600)',
  borderBottom: '1px solid var(--color-gray-200)',
  textAlign: 'center',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontSize: '14px',
  borderBottom: '1px solid var(--color-gray-100)',
  cursor: 'pointer',
};
